import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { detectLocalCurrency } from "@/lib/currency";

/**
 * Payment method picker. Routes buyers by where they are:
 *  - Nigeria (NGN) → Paystack in ₦ (local cards, bank transfer, USSD)
 *  - Everyone else → Flutterwave in $ (international cards) when admin enabled USD,
 *    otherwise Flutterwave in ₦.
 *  - Stripe shown only when the admin has it enabled (live key).
 * Backend is the source of truth for every amount.
 */
export default function PaymentMethodPicker({
  purpose,
  productId,
  quantity = 1,
  amountUsd,
  ngnLabel,
  onError,
  disabled,
  providers = { stripe: true, paystack: true, flutterwave: true },
  extraBody = {},
}) {
  const [busy, setBusy] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [flwInNaira, setFlwInNaira] = useState(false);

  useEffect(() => { api.get("/payments/config").then(({ data }) => setCfg(data)).catch(() => setCfg({})); }, []);

  const fail = (e) => {
    const msg = formatApiError(e.response?.data?.detail) || e.message;
    onError ? onError(msg) : toast.error(msg);
    setBusy(null);
  };
  const base = { purpose, product_id: productId, quantity, ...extraBody };
  const payStripe = () => { setBusy("stripe"); api.post("/payments/stripe/checkout", { ...base, origin_url: window.location.origin }).then(({ data }) => window.location.assign(data.checkout_url)).catch(fail); };
  const payPaystack = () => { setBusy("paystack"); api.post("/payments/initialize", base).then(({ data }) => window.location.assign(data.authorization_url)).catch(fail); };
  const payFlutterwave = () => { setBusy("flutterwave"); api.post("/flutterwave/checkout", { ...base, currency: cfg?.flutterwave_currency === "USD" && !flwInNaira ? "USD" : "NGN" }).then(({ data }) => window.location.assign(data.checkout_url)).catch(fail); };

  if (!cfg) return <div className="h-11 rounded-full bg-white/5 animate-pulse" data-testid="pay-loading" />;

  const local = detectLocalCurrency();
  const flwUsd = cfg.flutterwave_currency === "USD";
  const rate = cfg.usd_to_ngn_rate;
  const nairaLabel = ngnLabel || (rate ? `NGN ${Math.round(amountUsd * rate).toLocaleString()}` : `$${amountUsd.toFixed(2)}`);
  const usdLabel = `$${amountUsd.toFixed(2)}`;
  const showStripe = providers.stripe && cfg.stripe_enabled;
  const nigerian = local === "NGN";

  const buttons = [];
  if (providers.paystack) buttons.push({ key: "paystack", onClick: payPaystack, dot: <Dot color="#0BA4DB" />, amount: nairaLabel, name: "Paystack", hint: "Naira cards · bank transfer · USSD", recommended: nigerian });
  if (providers.flutterwave) buttons.push({ key: "flutterwave", onClick: payFlutterwave, dot: <Dot color="#F5A623" />, amount: flwUsd && !flwInNaira ? usdLabel : nairaLabel, name: "Flutterwave", hint: flwUsd && !flwInNaira ? "International cards · charged in USD" : "Cards · bank transfer · mobile money", recommended: !nigerian && (flwUsd || !showStripe) });
  if (showStripe) buttons.push({ key: "stripe", onClick: payStripe, dot: <StripeIcon />, amount: usdLabel, name: "Stripe", hint: "International cards · Apple / Google Pay", recommended: !nigerian && !flwUsd });
  buttons.sort((a, b) => Number(b.recommended) - Number(a.recommended));

  return (
    <div className="space-y-2" data-testid="payment-method-picker">
      {buttons.map((b, i) => (
        <Button
          key={b.key}
          onClick={b.onClick}
          disabled={disabled || !!busy}
          variant={i === 0 ? "default" : "outline"}
          data-testid={`pay-${b.key}-btn`}
          className={`w-full h-auto py-2.5 rounded-2xl font-semibold flex items-center justify-between gap-3 px-4 ${i === 0 ? "bg-white text-black hover:bg-neutral-200" : "bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white"}`}
        >
          <span className="flex items-center gap-2.5 text-left">
            {b.dot}
            <span className="flex flex-col leading-tight">
              <span>{busy === b.key ? `Redirecting to ${b.name}…` : <>Pay <span className="font-mono">{b.amount}</span> · {b.name}</>}</span>
              <span className={`text-[11px] font-normal ${i === 0 ? "text-neutral-600" : "text-neutral-500"}`}>{b.hint}</span>
            </span>
          </span>
          {b.recommended && <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${i === 0 ? "bg-black/10 text-black" : "border border-white/20 text-neutral-300"}`} data-testid={`pay-${b.key}-recommended`}>Recommended</span>}
        </Button>
      ))}
      {providers.flutterwave && flwUsd && (
        <button type="button" onClick={() => setFlwInNaira((v) => !v)} className="text-[11px] text-neutral-500 hover:text-white underline underline-offset-2" data-testid="flw-currency-toggle">
          {flwInNaira ? "Pay in US dollars via Flutterwave instead" : "Pay in Naira via Flutterwave instead"}
        </button>
      )}
    </div>
  );
}

function Dot({ color }) {
  return <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: color }} />;
}

function StripeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
      <path fill="#635BFF" d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631V2.66C16.798 2.146 14.844 1.9 12.564 1.9c-3.86 0-6.36 2.014-6.36 5.417 0 5.298 7.319 4.463 7.319 6.732 0 .994-.87 1.297-2.146 1.297-2.394 0-5.472-.99-7.207-1.85v4.484c1.916.827 3.86 1.184 6.203 1.184 3.955 0 6.63-1.973 6.63-5.428-.014-5.752-7.383-4.732-7.383-6.63l.356.044z"/>
    </svg>
  );
}
